'use client';

import { create } from 'zustand';
import { User } from '@/db/schema';
import { persist } from 'zustand/middleware';
import { supabase } from '@/db/supabase';

interface UserState {
  currentUser: User | null;
  isLoading: boolean;
  error: string | null;
  
  fetchUserByEmail: (email: string) => Promise<User | null>;
  createUser: (user: { email: string; name?: string; avatarUrl?: string }) => Promise<User | null>;
  updateUser: (user: Partial<User> & { id: number }) => Promise<User | null>;
  setCurrentUser: (user: User | null) => void;
  logout: () => Promise<void>;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      currentUser: null,
      isLoading: false,
      error: null,
      
      fetchUserByEmail: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`/api/users?email=${encodeURIComponent(email)}`);
          if (!response.ok) throw new Error('Failed to fetch user');
          
          const user = await response.json();
          if (user) {
            set({ currentUser: user, isLoading: false });
          } else {
            set({ isLoading: false });
          }
          return user;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'An unknown error occurred', 
            isLoading: false 
          });
          return null;
        }
      },
      
      createUser: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/users', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create user');
          }
          
          const newUser = await response.json();
          set({ currentUser: newUser, isLoading: false });
          return newUser;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'An unknown error occurred', 
            isLoading: false 
          });
          return null;
        }
      },
      
      updateUser: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/users', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update user');
          }
          
          const updatedUser = await response.json();
          set({ currentUser: updatedUser, isLoading: false });
          return updatedUser;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'An unknown error occurred', 
            isLoading: false 
          });
          return null;
        }
      },
      
      setCurrentUser: (user) => {
        set({ currentUser: user });
      },
      
      logout: async () => {
        set({ isLoading: true });
        try {
          await supabase.auth.signOut();
          set({ currentUser: null, isLoading: false });
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'An unknown error occurred', 
            isLoading: false 
          });
        }
      },
    }),
    {
      name: 'user-storage',
    }
  )
);
